// What the notices part keeps doing while the app is open, drawing nothing:
// showing the notices that reach this screen as OS notifications, following a
// clicked desktop notification, and keeping the app's badge.
//
// How a notice gets here: the part's realtime handler receives `notice.show`
// but only has the query client, and showing it needs the router (a click
// opens its screen). Instead of a module-level store, the handler hands the
// payload on as a DOM event of this window (`handOnNotice`), and this
// component, mounted once inside the router, shows it. A window event fits:
// an OS notification belongs to the window, nothing is kept, and two apps in
// one page (tests) never share state through a module.
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useApi } from "../app/context";
import { desktopShell, noticeDesktop } from "../device/desktop-shell";
import {
  navigateFromNotice,
  notificationPermission,
  setAppBadge,
  showOsNotice,
} from "../device/os-notices";
import { isWindowInFront } from "../device/window-front";
import { noticeToShow, osNoticesOn, osNoticesSetting } from "./decide";
import { badgeQuery } from "./queries";

const NOTICE_EVENT = "trameria:notice-show";

/** Called by the realtime handler of `notice.show`. */
export function handOnNotice(payload: unknown): void {
  window.dispatchEvent(new CustomEvent(NOTICE_EVENT, { detail: payload }));
}

export function NoticesBackground() {
  const router = useRouter();
  const api = useApi();
  const count = useQuery(badgeQuery(api)).data?.count;

  // frontend-development: not the server's stream (the part's realtime handler receives it); shows what it hands on and follows the desktop's clicks.
  useEffect(() => {
    const go = (url: string) => navigateFromNotice(router, url);
    const onNotice = (event: Event) => {
      const desktop = noticeDesktop();
      const shown = noticeToShow({
        payload: (event as CustomEvent).detail,
        installationId: desktop?.installationId ?? null,
        inFront: isWindowInFront(),
        pathname: router.state.location.pathname,
        enabled: osNoticesOn(osNoticesSetting.read(), {
          inDesktopApp: desktop !== null,
          permission: notificationPermission(),
        }),
      });
      if (shown) showOsNotice(shown, go);
    };
    window.addEventListener(NOTICE_EVENT, onNotice);
    const offNavigate = desktopShell()?.onNavigate?.(go);
    return () => {
      window.removeEventListener(NOTICE_EVENT, onNotice);
      offNavigate?.();
    };
  }, [router]);

  // frontend-development: a write to the OS (the badge); the count is read by badgeQuery.
  useEffect(() => {
    if (count !== undefined) setAppBadge(count);
  }, [count]);

  return null;
}
